import { ContractIcon } from 'assets/icons/contracts';
import { HomeIcon } from 'assets/icons/home';
import { OpportunityOverviewIcon } from 'assets/icons/opportunity-overview';
import { PolicyManagementIcon } from 'assets/icons/policy-management';
import { PortfolioAnalysisIcon } from 'assets/icons/portfolio-analysis';
import { ReferenceDataIcon } from 'assets/icons/reference-data';
import { SettingsIcon } from 'assets/icons/settings';
import { ToolsAndUtilitiesIcon } from 'assets/icons/tools-and-utilities';
import Sidebar from 'components/features/sidebar';
import React from 'react';
import { EPath } from 'constants/path';
import Header from 'components/ui/header';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDashboardFiltersStore } from 'stores/dashboard-filters';

interface HomeLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  allowBack?: boolean;
  backDestinationPath?: string;
  loading?: boolean;
  backNavigationState?: object;
}

const HomeLayout: React.FC<HomeLayoutProps> = ({
  children,
  allowBack,
  title,
  subtitle,
  backDestinationPath,
  loading = false,
  backNavigationState,
}) => {
  const { t } = useTranslation();
  const { resetAllDashboardFilters } = useDashboardFiltersStore();

  const sideBarItems = [
    {
      id: 1,
      title: t('sidebar.home'),
      icon: HomeIcon,
      href: EPath.HOME,
      subItems: [
        {
          id: 1,
          title: 'Dashboard',
          href: EPath.DASHBOARD,
          onClick: resetAllDashboardFilters,
        },
      ],
    },
    {
      id: 2,
      title: t('sidebar.policyManagement'),
      icon: PolicyManagementIcon,
      href: EPath.POLICY_MANAGEMENT,
      subItems: [
        {
          id: 1,
          title: t('sidebar.viewMasterContract'),
          href: EPath.VIEW_MASTER_CONTRACT,
        },
        {
          id: 2,
          title: t('sidebar.viewBinderContract'),
          href: EPath.VIEW_BINDER_CONTRACT,
          onClick: resetAllDashboardFilters,
        },
      ],
    },
    {
      id: 3,
      title: t('sidebar.opportunity'),
      icon: OpportunityOverviewIcon,
      href: EPath.OPPORTUNITY,
      subItems: [
        {
          id: 1,
          title: t('sidebar.createNewOpportunity'),
          href: EPath.NEW_OPPORTUNITY,
        },
      ],
    },
    {
      id: 4,
      title: t('sidebar.submissions'),
      icon: ContractIcon,
      href: EPath.SUBMISSIONS,
      subItems: [
        {
          id: 1,
          title: t('sidebar.createNewSubmission'),
          href: EPath.NEW_SUBMISSION,
        },
        {
          id: 2,
          title: t('sidebar.cloneSubmission'),
          href: EPath.CLONE_SUBMISSION,
        },
      ],
    },
    {
      id: 5,
      title: t('sidebar.renewals'),
      icon: ReferenceDataIcon,
      href: EPath.RENEWALS,
      subItems: [
        {
          id: 1,
          title: t('sidebar.createRenewalSubmission'),
          href: EPath.NEW_RENEWAL_SUBMISSION,
        },
        {
          id: 2,
          title: t('sidebar.cloneRenewalSubmission'),
          href: EPath.CLONE_RENEWAL_SUBMISSION,
        },
      ],
    },
    {
      id: 6,
      title: t('sidebar.premiumReceipting'),
      icon: ContractIcon,
      href: EPath.PREMIUM_RECEIPTING,
      subItems: [
        {
          id: 1,
          title: t('sidebar.receipts'),
          href: EPath.LIST_RECEIPTS,
        },
        {
          id: 2,
          title: t('sidebar.createNewReceipt'),
          href: EPath.NEW_RECEIPT,
        },
        {
          id: 3,
          title: t('sidebar.approveClaimsPayments'),
          href: EPath.APPROVE_CLAIMS_PAYMENTS,
        },
      ],
    },
    {
      id: 7,
      title: t('sidebar.claims'),
      icon: ContractIcon,
      href: EPath.CLAIMS,
      subItems: [
        {
          id: 1,
          title: t('sidebar.claimsPaymentsDashboard'),
          href: EPath.CLAIMS_PAYMENTS_DASHBOARD,
        },
        {
          id: 2,
          title: t('sidebar.registerNewMasterClaim'),
          href: EPath.REGISTER_NEW_MASTER_CLAIM,
        },
        {
          id: 3,
          title: t('sidebar.updateMasterClaim'),
          href: EPath.UPDATE_MASTER_CLAIM,
        },
        {
          id: 4,
          title: t('sidebar.closeMasterClaim'),
          href: EPath.CLOSE_MASTER_CLAIM,
        },
        {
          id: 5,
          title: t('sidebar.updateBinderClaim'),
          href: EPath.UPDATE_BINDER_CLAIM,
        },
        {
          id: 6,
          title: t('sidebar.closeBinderClaim'),
          href: EPath.CLOSE_BINDER_CLAIM,
        },
      ],
    },
    {
      id: 8,
      title: t('sidebar.bordereauxReports'),
      icon: ContractIcon,
      href: EPath.BORDEREAUX_REPORTS,
      subItems: [
        {
          id: 1,
          title: t('sidebar.newBordereaux'),
          href: EPath.NEW_BORDEREAUX,
        },
        {
          id: 2,
          title: t('sidebar.existingBordereaux'),
          href: EPath.EXISTING_BORDEREAUX,
        },
      ],
    },
    {
      id: 9,
      title: t('sidebar.portfolioAnalysis'),
      icon: PortfolioAnalysisIcon,
      href: EPath.PORTFOLIO_ANALYSIS,
    },
    {
      id: 10,
      title: t('sidebar.referenceData'),
      icon: ReferenceDataIcon,
      href: EPath.REFERENCE_DATA,
      subItems: [
        {
          id: 1,
          title: t('sidebar.insuredRecords'),
          href: EPath.INSURED_RECORDS,
        },
        {
          id: 2,
          title: t('sidebar.reinsuredRecords'),
          href: EPath.REINSURED_RECORDS,
        },
        {
          id: 3,
          title: t('sidebar.brokerRecords'),
          href: EPath.BROKER_RECORDS,
        },
        {
          id: 4,
          title: t('sidebar.binderRecords'),
          href: EPath.BINDER_RECORDS,
        },
        {
          id: 5,
          title: t('sidebar.currency'),
          href: EPath.CURRENCY,
        },
        {
          id: 6,
          title: t('sidebar.pricingAssumptions'),
          href: EPath.PRICING_ASSUMPTIONS,
          subItems: [
            {
              id: 1,
              title: t('sidebar.fireSeverityCurves'),
              href: EPath.FIRE_SEVERITY_CURVES,
            },
            {
              id: 2,
              title: t('sidebar.catZoneRates'),
              href: EPath.CAT_ZONE_RATES,
            },
            {
              id: 3,
              title: t('sidebar.fireRates'),
              href: EPath.FIRE_RATES,
            },
            {
              id: 4,
              title: t('sidebar.adequacyAssumptions'),
              href: EPath.ADEQUACY_ASSUMPTIONS,
            },
            {
              id: 5,
              title: t('sidebar.referenceDataPricing'),
              href: EPath.REFERENCE_DATA_PRICING,
            },
          ],
        },
        {
          id: 7,
          title: t('catEvent.catEventRecords'),
          href: EPath.CAT_EVENT_RECORDS,
        },
      ],
    },
    {
      id: 11,
      title: t('sidebar.toolsAndUtilities'),
      icon: ToolsAndUtilitiesIcon,
      href: EPath.TOOLS_AND_UTILITIES,
    },
    {
      id: 12,
      title: t('sidebar.settings'),
      icon: SettingsIcon,
      href: EPath.SETTINGS,
    },
  ];

  return (
    <>
      <div className="flex">
        <div className="fixed top-0 left-0">
          <Sidebar items={sideBarItems} />
        </div>
        <div className="flex-grow ml-w_sidebar max-w-content px-5">
          <Header
            title={title}
            subtitle={subtitle}
            allowBack={allowBack}
            backDestinationPath={backDestinationPath}
            backNavigationState={backNavigationState}
          />
          <Spin spinning={loading} />
          {!loading && children}
        </div>
      </div>
    </>
  );
};

export default HomeLayout;
